<template lang="pug">
.historial-prestacional
  spinner(v-if="isLoading")

  template(v-else-if="memberId")
    //- Filtros y resumen
    .filters-bar
      .filter-group
        label Año
        el-select(v-model="filterYear" size="small" clearable placeholder="Todos" @change="loadRecords")
          el-option(v-for="y in yearOptions" :key="y" :label="y" :value="y")
        label Mes
        el-select(v-model="filterMonth" size="small" clearable placeholder="Todos" @change="loadRecords")
          el-option(v-for="m in monthOptions" :key="m.value" :label="m.label" :value="m.value")
      .summary-bar(v-if="records.length")
        .summary-item
          span.summary-label Prestaciones
          span.summary-val {{ records.length }}
        .summary-item
          span.summary-label Total período
          span.summary-val.total $ {{ totalAmount }}

    //- Tabla
    .empty-state(v-if="!records.length")
      i.el-icon-document
      p Sin prestaciones en el período seleccionado
    .records-list(v-else)
      .record-card(v-for="sr in records" :key="sr._id")
        .record-col-date
          .record-date {{ sr.createdAt | formatDate }}
          .record-trx {{ sr.transactionCode || '—' }}
        .record-col-practice
          .record-practice
            span.practice-code {{ sr.practiceSnapshot && sr.practiceSnapshot.code || '—' }}
            span.practice-name {{ sr.practiceSnapshot && sr.practiceSnapshot.name || '—' }}
          .record-row-bottom
            .provider-item(v-if="solicitante(sr)")
              span.provider-label Solicitante
              span.provider-name {{ solicitante(sr) }}
            .provider-item(v-if="ejecutor(sr)")
              span.provider-label Prestador
              span.provider-name {{ ejecutor(sr) }}
        .record-col-amount
          span.amount-val {{ sr.expectedAmount ? '$ ' + sr.expectedAmount.toLocaleString('es-AR') : '—' }}
        .record-col-tags
          el-tag.record-origin(size="small" :type="sr.origin === 'authorization' ? 'warning' : 'success'")
            | {{ sr.origin === 'authorization' ? 'Autorización' : 'Directa' }}
          router-link.ver-link(
            v-if="sr.authorizationCase"
            :to="{ name: 'audit-detail', params: { id: sr.authorizationCase._id || sr.authorizationCase } }"
          ) Ver →

  .empty-state(v-else)
    i.el-icon-document
    p Este paciente no tiene historial prestacional vinculado
</template>

<script>
import Spinner from "@/components/ui/Spinner";
import serviceRecordService from "@/services/service-record";

/**
 * @component HistorialPrestacional
 * @description Muestra el historial de prestaciones recibidas por un paciente,
 * con filtros por período, resumen de importes, y detalle de prestadores
 * solicitante y ejecutor. Solo visible para Admin y Auditor médico.
 */
export default {
  name: "HistorialPrestacional",
  components: { Spinner },
  filters: {
    /**
     * @filter formatDate
     * @description Formatea fecha ISO a dd/mm/yyyy HH:mm en locale es-AR
     */
    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      return (
        d.toLocaleDateString("es-AR") +
        " " +
        d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })
      );
    },
  },
  props: {
    /** @prop {String} patientId - ID del User paciente */
    patientId: { type: String, required: true },
    /** @prop {String} memberId - ID del Member vinculado al paciente */
    memberId: { type: String, default: null },
  },
  data() {
    return {
      isLoading: false,
      records: [],
      filterYear: new Date().getFullYear(),
      filterMonth: null,
    };
  },
  computed: {
    /**
     * @computed yearOptions
     * @description Años disponibles para filtrar (últimos 3 años)
     */
    yearOptions() {
      const current = new Date().getFullYear();
      return [current - 2, current - 1, current];
    },
    /**
     * @computed monthOptions
     * @description Meses del año para filtrar
     */
    monthOptions() {
      return [
        { value: 1, label: "Enero" },
        { value: 2, label: "Febrero" },
        { value: 3, label: "Marzo" },
        { value: 4, label: "Abril" },
        { value: 5, label: "Mayo" },
        { value: 6, label: "Junio" },
        { value: 7, label: "Julio" },
        { value: 8, label: "Agosto" },
        { value: 9, label: "Septiembre" },
        { value: 10, label: "Octubre" },
        { value: 11, label: "Noviembre" },
        { value: 12, label: "Diciembre" },
      ];
    },
    /**
     * @computed totalAmount
     * @description Suma de importes del período filtrado
     */
    totalAmount() {
      return this.records
        .reduce((acc, sr) => acc + (sr.expectedAmount || 0), 0)
        .toLocaleString("es-AR");
    },
  },
  watch: {
    memberId: {
      immediate: true,
      handler(val) {
        if (val) this.loadRecords();
      },
    },
  },
  methods: {
    /**
     * @method loadRecords
     * @description Carga ServiceRecords del miembro con filtros de período opcionales
     */
    async loadRecords() {
      this.isLoading = true;
      try {
        const params = { memberId: this.memberId };
        if (this.filterYear) params.billingYear = this.filterYear;
        if (this.filterMonth) params.billingMonth = this.filterMonth;
        const data = await serviceRecordService.get(params);
        this.records = data.docs || data;
      } catch (e) {
        this.records = [];
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * @method solicitante
     * @description Resuelve el nombre del prestador solicitante desde AuthorizationCase
     */
    solicitante(sr) {
      const ac = sr.authorizationCase;
      if (!ac) return null;
      if (ac.providerSnapshot && ac.providerSnapshot.firstName) {
        return `${ac.providerSnapshot.lastName}, ${ac.providerSnapshot.firstName}`;
      }
      if (ac.provider && ac.provider.firstName) {
        return `${ac.provider.lastName}, ${ac.provider.firstName}`;
      }
      return null;
    },
    /**
     * @method ejecutor
     * @description Resuelve el nombre del prestador ejecutor desde ServiceRecord
     */
    ejecutor(sr) {
      if (sr.providerSnapshot && sr.providerSnapshot.firstName) {
        return `${sr.providerSnapshot.lastName}, ${sr.providerSnapshot.firstName}`;
      }
      if (sr.provider && sr.provider.firstName) {
        return `${sr.provider.lastName}, ${sr.provider.firstName}`;
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.historial-prestacional {
  padding: 8px 0;
}

.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}
.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  label {
    font-size: 12px;
    color: #6b7294;
    font-weight: 500;
  }
}
.summary-bar {
  display: flex;
  gap: 24px;
}
.summary-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.summary-label {
  font-size: 11px;
  color: #6b7294;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.summary-val {
  font-size: 16px;
  font-weight: 700;
  color: #1a1f36;
}
.summary-val.total {
  color: #1d9e75;
  font-size: 18px;
}

.empty-state {
  padding: 48px 32px;
  text-align: center;
  color: #6b7294;
  i {
    font-size: 32px;
    margin-bottom: 12px;
    display: block;
  }
  p {
    font-size: 14px;
    margin: 0;
  }
}

.records-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-card {
  display: grid;
  grid-template-columns: 160px 1fr 100px 140px;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  }
}

.record-col-date {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.record-date {
  font-size: 12px;
  color: #6b7294;
  white-space: nowrap;
}
.record-trx {
  font-family: monospace;
  font-size: 11px;
  color: #05c49a;
  font-weight: 600;
}

.record-col-practice {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}
.record-practice {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  .practice-code {
    font-size: 12px;
    font-weight: 700;
    color: #002d5e;
    background: #eef3ff;
    padding: 2px 7px;
    border-radius: 4px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .practice-name {
    font-size: 13px;
    color: #1a1f36;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
}
.record-row-bottom {
  display: flex;
  gap: 16px;
}
.provider-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
}
.provider-label {
  font-weight: 600;
  color: #6b7294;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.provider-name {
  color: #1a1f36;
}

.record-col-amount {
  text-align: right;
  border-left: 2px solid #e2e8f0;
  border-right: 2px solid #e2e8f0;
  padding: 0 12px;
}
.amount-val {
  font-size: 15px;
  font-weight: 700;
  color: #1d9e75;
}

.record-col-tags {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}
.ver-link {
  font-size: 12px;
  color: #002d5e;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
}
</style>
